import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { usePageSEO, SITE_CONFIG } from '../../utils/seoUtils';

/**
 * Composant pour gérer les meta tags SEO de manière dynamique
 * Utilise react-helmet-async pour modifier le <head> de la page
 */
const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  image, 
  canonical,
  structuredData,
  noindex = false,
  article = null // Pour les articles spécifiques
}) => {
  const location = useLocation();
  const pageSEO = usePageSEO(location.pathname);
  
  // Utiliser les props ou les valeurs par défaut de la page
  const finalTitle = title || pageSEO.title;
  const finalDescription = description || pageSEO.description;
  const finalKeywords = keywords || pageSEO.keywords;
  const finalCanonical = canonical || pageSEO.canonical;
  const finalImage = image || `${SITE_CONFIG.baseUrl}${SITE_CONFIG.defaultImage}`;
  const finalStructuredData = structuredData || pageSEO.structuredData;

  return (
    <Helmet>
      {/* Meta tags de base */}
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <link rel="canonical" href={finalCanonical} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Open Graph */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:url" content={finalCanonical} />
      <meta property="og:site_name" content={SITE_CONFIG.siteName} />
      <meta property="og:type" content={article ? "article" : "website"} />
      <meta property="og:locale" content={SITE_CONFIG.locale} />
      
      {/* Meta tags spécifiques aux articles */}
      {article && (
        <>
          <meta property="article:published_time" content={article.created} />
          <meta property="article:modified_time" content={article.updated} />
          <meta property="article:author" content={SITE_CONFIG.siteName} />
          {article.expand?.category && (
            <meta property="article:section" content={article.expand.category.name} />
          )}
        </>
      )}
      
      {/* Twitter Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      {SITE_CONFIG.twitterHandle && (
        <meta name="twitter:site" content={SITE_CONFIG.twitterHandle} />
      )}
      
      {/* Structured Data */}
      {finalStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(finalStructuredData)}
        </script>
      )}
      
      {/* Hreflang pour le français */}
      <link rel="alternate" hrefLang="fr" href={finalCanonical} />
      <link rel="alternate" hrefLang="x-default" href={finalCanonical} />
    </Helmet>
  );
};

export default SEOHead;
